import React from "react";
import { Stage, Layer, Line } from "react-konva";
import PhotoNotebookViewport from "./PhotoNotebookViewport";

function formatTutorText(value) {
  return String(value || "")
    .replace(/\$\$?/g, "")
    .replace(/\\left|\\right/g, "")
    .replace(/\\cdot/g, " * ")
    .replace(/\\times/g, " * ")
    .replace(/\\frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, "$1/$2")
    .replace(/\\sqrt\s*\{([^{}]+)\}/g, "sqrt($1)")
    .replace(/\\(sin|cos|tan|sec|cosec|cot|theta|alpha|beta|gamma|pi)\b/g, "$1")
    .replace(/\\,/g, " ")
    .replace(/[{}]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function getTutorHintCategory(tutor, hint) {
  const existingTitle = hint?.title || tutor?.summary?.title || hint?.targetNodeTitle || tutor?.targetNode?.title;
  if (
    existingTitle &&
    typeof existingTitle === "string" &&
    !["hint", "guidance", "note", "next step", "use identity"].includes(existingTitle.trim().toLowerCase())
  ) {
    return existingTitle.trim();
  }

  const source = [
    hint?.title,
    hint?.targetNodeTitle,
    tutor?.targetNode?.title,
    tutor?.summary?.title,
    tutor?.summary?.message,
    tutor?.summary?.followUp,
    ...(Array.isArray(tutor?.targetNode?.conceptTags) ? tutor.targetNode.conceptTags : [])
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const checks = [
    [/\belimin/i, "Elimination"],
    [/\bsubstitut/i, "Substitution"],
    [/\bsimplif/i, "Simplification"],
    [/\bidentity\b/i, "Use identity"],
    [/\b(sec|sin|cos|tan|csc|cot)\b/i, "Trigonometric relation"],
    [/\bfactor/i, "Factoring"],
    [/\bexpand/i, "Expansion"],
    [/\bisolate/i, "Isolation"],
    [/\brearrange/i, "Rearrangement"],
    [/\bsolve/i, "Solve"],
    [/\bevaluate/i, "Evaluate"],
    [/\bsetup\b/i, "Set up"],
    [/\bequation\b/i, "Set up"]
  ];
  const match = checks.find(([regex]) => regex.test(source));
  if (match) return match[1];
  return existingTitle || "Hint";
}

function getTutorPrimaryGuidance(tutor) {
  const guidance = tutor?.guidance || {};
  if (guidance?.showError && guidance?.error) {
    return {
      kind: "error",
      label: "Error",
      title: "Correct this step",
      message: guidance.error.message,
      detail: guidance.error.detail,
      targetTitle: guidance.error.targetNodeTitle || tutor?.targetNode?.title || ""
    };
  }
  const isComplete =
    tutor?.studentState === "complete" ||
    guidance?.recommendedPresentation === "complete" ||
    String(tutor?.summary?.title || "").toLowerCase().includes("complete") ||
    String(guidance?.hint?.title || "").toLowerCase().includes("complete");

  if (isComplete) {
    return {
      kind: "complete",
      label: "Done",
      title: tutor?.summary?.title || guidance?.hint?.title || "Solution Complete!",
      message: tutor?.summary?.message || guidance?.hint?.message || "Excellent work! Solution completed.",
      detail: null,
      targetTitle: ""
    };
  }
  if (guidance?.showHint && guidance?.hint) {
    return {
      kind: "hint",
      label: "Hint",
      title: getTutorHintCategory(tutor, guidance.hint),
      message: guidance.hint.message,
      detail: null,
      targetTitle: guidance.hint.targetNodeTitle || tutor?.targetNode?.title || ""
    };
  }
  return {
    kind: "note",
    label: tutor?.studentState === "complete" ? "Done" : "Hint",
    title: getTutorHintCategory(tutor, null),
    message: tutor?.summary?.message || tutor?.summary?.followUp || "",
    detail: null,
    targetTitle: tutor?.targetNode?.title || ""
  };
}

function isTutorExplanationEntry(entry) {
  if (!entry) return false;
  if (entry.intent === "explain_deeper" || entry.tutor?.intent === "explain_deeper") return true;
  const status = String(entry.status || "").toLowerCase();
  if (status === "explanation" || status === "explain") return true;
  const rawTitle = String(
    entry.tutor?.guidance?.hint?.title ||
    entry.tutor?.summary?.title ||
    entry.title ||
    ""
  ).toLowerCase().trim();
  if (rawTitle === "deeper explanation" || rawTitle.includes("deeper breakdown")) return true;
  return false;
}

function groupTutorHistoryHierarchically(history) {
  if (!Array.isArray(history) || history.length === 0) return [];
  const groups = [];

  for (let i = 0; i < history.length; i++) {
    const entry = history[i];
    const isExplanation = isTutorExplanationEntry(entry);

    if (isExplanation && groups.length > 0) {
      const parentGroup = groups[groups.length - 1];
      parentGroup.explanations.push(entry);
    } else {
      groups.push({
        ...entry,
        explanations: []
      });
    }
  }

  return groups.reverse();
}

function SlateCanvasPanel({
  slateProfile,
  slateFullScreen,
  strokeTool,
  showPenMenu,
  strokeColor,
  lines,
  redoStack,
  liveOcrStatus,
  isDrawing,
  showSlateDiagnostics,
  stageCanvasWidth,
  stageSize,
  laneBounds,
  remotePreviewLines,
  laneDragBoundary,
  slateRowDiagnostics,
  selectedDiagStep,
  toolbarMenuRef,
  slateRef,
  canvasPaneRef,
  stageRef,
  activeStrokeCanvasRef,
  diagnosticRailRef,
  setStrokeTool,
  setShowPenMenu,
  setStrokeColor,
  handleUndoStroke,
  handleRedoStroke,
  setSlateFullScreen,
  handlePointerDown,
  handlePointerMove,
  handlePointerUp,
  handleRuleDragStart,
  setSelectedDiagStep,
  analyzeActions,
  ruleStroke,
  ruleStrokeWidth,
  ruleDash,
  diagnosticRailWidth,
  onClearSlate,
  clearSlateDisabled,
  showClearConfirm,
  onCancelClearSlate,
  onConfirmClearSlate,
  tutorLaneIndex,
  onTutorHintClick,
  onSlateScrollNearEnd,
  tutorHintDisabled,
  isHinting,
  hintingIntent,
  tutorChatOpen,
  onTutorChatToggle,
  onTutorChatOpen,
  tutorHintData,
  tutorHintHistory,
  tutorModeMeta,
  plainCanvas = true,
  stepClusters = [],
  paperStyle = "white",
  setPaperStyle,
  workTab = "slate",
  setWorkTab,
  editorContent = null,
  onOpenPhotoModal = null,
  isPhotoSubmissionActive = false,
  photoSubmissionPages = [],
  isAnalyzing = false,
  analysisStatus = "",
  photoProcessingPhase = "",
  detectedStepBoxes = [],
  stepDiagnostics = {}
}) {
  const isScrollMode = strokeTool === "scroll";
  const runwayRequestRef = React.useRef(false);
  const [viewportScrollLeft, setViewportScrollLeft] = React.useState(0);
  const [showPaperMenu, setShowPaperMenu] = React.useState(false);

  React.useEffect(() => {
    function handlePointerDownOutside(event) {
      if (!showPaperMenu) return;
      const host = toolbarMenuRef?.current;
      if (host && !host.contains(event.target)) {
        setShowPaperMenu(false);
      }
    }
    window.addEventListener("pointerdown", handlePointerDownOutside);
    return () => window.removeEventListener("pointerdown", handlePointerDownOutside);
  }, [showPaperMenu, toolbarMenuRef]);

  React.useEffect(() => {
    runwayRequestRef.current = false;
  }, [stageSize.height]);

  React.useEffect(() => {
    const host = slateRef?.current;
    if (!host) return undefined;

    let rafId = 0;
    const syncScrollState = () => {
      rafId = 0;
      setViewportScrollLeft(host.scrollLeft || 0);

      const distanceToBottom = host.scrollHeight - host.scrollTop - host.clientHeight;
      if (
        typeof onSlateScrollNearEnd === "function" &&
        host.scrollTop > 8 &&
        distanceToBottom < 420 &&
        !runwayRequestRef.current
      ) {
        runwayRequestRef.current = true;
        onSlateScrollNearEnd();
      }
    };

    syncScrollState();

    const handleHostScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(syncScrollState);
    };

    host.addEventListener("scroll", handleHostScroll, { passive: true });
    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(syncScrollState)
      : null;
    resizeObserver?.observe(host);
    window.addEventListener("resize", syncScrollState);
    return () => {
      host.removeEventListener("scroll", handleHostScroll);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", syncScrollState);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [onSlateScrollNearEnd, slateRef, showSlateDiagnostics, stageSize.height, stageCanvasWidth]);

  const panStartRef = React.useRef(null);

  const handleStagePointerDown = (e) => {
    if (!isScrollMode || !slateRef?.current) return;
    if (e.button !== 0 && e.pointerType === "mouse") return;
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: slateRef.current.scrollLeft,
      scrollTop: slateRef.current.scrollTop
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {}
  };

  const handleStagePointerMove = (e) => {
    if (!isScrollMode || !panStartRef.current || !slateRef?.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    slateRef.current.scrollLeft = panStartRef.current.scrollLeft - dx;
    slateRef.current.scrollTop = panStartRef.current.scrollTop - dy;
  };

  const handleStagePointerUp = (e) => {
    if (!panStartRef.current) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) {}
    panStartRef.current = null;
  };

  const normalizedTutorLaneIndex = Number.isFinite(Number(tutorLaneIndex)) ? Number(tutorLaneIndex) : 0;
  const clusters = Array.isArray(stepClusters) ? stepClusters : [];

  const tutorLane =
    laneBounds.find((lane) => Number(lane.index) === normalizedTutorLaneIndex) ||
    laneBounds[0] ||
    { top: 0, bottom: 90 };
  const tutorLaneTop = Number.isFinite(Number(tutorLane.top)) ? Number(tutorLane.top) : 0;
  const tutorLaneBottom = Number.isFinite(Number(tutorLane.bottom))
    ? Number(tutorLane.bottom)
    : tutorLaneTop + 90;

  let tutorHintTop = 64;
  if (plainCanvas) {
    if (selectedDiagStep) {
      const selected = clusters.find((c) => Number(c.stepNo) === Number(selectedDiagStep));
      if (selected?.bounds) {
        tutorHintTop = Math.max(32, Math.round((selected.bounds.minY + selected.bounds.maxY) / 2));
      }
    } else if (clusters.length > 0) {
      const last = clusters[clusters.length - 1];
      tutorHintTop = Math.max(32, Math.round(last.bounds.maxY + 48));
    }
  } else {
    tutorHintTop = Math.max(32, Math.round((tutorLaneTop + tutorLaneBottom) / 2));
  }
  const tutorHistory = Array.isArray(tutorHintHistory) ? tutorHintHistory : [];
  const groupedTutorHistory = groupTutorHistoryHierarchically(tutorHistory);
  const isExplainingDeeper = Boolean(isHinting) && hintingIntent === "explain_deeper";
  const isNormalHinting = Boolean(isHinting) && !isExplainingDeeper;
  return (
    <div className={`slate-shell slate-container ${slateFullScreen ? "full-screen" : ""} slate-mode profile-${slateProfile}`}>
      {/* 1. Top Handle Frame Row - Sits outside the slate frame in proportionate size */}
      <div className="slate-handle-row">
        <div className="slate-top-handle" role="toolbar" aria-label="Writing Tools Handle">
          <div className="slate-handle-tools">
            <div className="slate-pen-menu">
              <button
                id="slate-btn-pen"
                type="button"
                className={`slate-handle-btn ${strokeTool === "pen" && workTab === "slate" ? "active" : ""}`}
                title="Pen"
                onClick={() => {
                  if (workTab !== "slate" && typeof setWorkTab === "function") setWorkTab("slate");
                  setStrokeTool("pen");
                  setShowPaperMenu(false);
                  setShowPenMenu((prev) => !prev);
                }}
              >
                <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                  <path
                    d="M4 17.2V20h2.8L17.6 9.2l-2.8-2.8L4 17.2z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <path
                    d="M13.9 7.1l2.8 2.8M15.6 5.4l2.8 2.8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="slate-pen-swatch-dot" style={{ backgroundColor: strokeColor }} />
              </button>
              {showPenMenu && (
                <div className="slate-pen-dropdown">
                  <div className="slate-pen-swatches" role="listbox" aria-label="Pen colors">
                    {[
                      { color: "#1d4ed8", label: "Blue pen" },
                      { color: "#0f172a", label: "Black pen" }
                    ].map((penOption) => (
                      <button
                        key={penOption.color}
                        type="button"
                        role="option"
                        aria-label={penOption.label}
                        aria-selected={strokeColor === penOption.color}
                        className={`slate-pen-swatch ${strokeColor === penOption.color ? "active" : ""}`}
                        onClick={() => {
                          setStrokeColor(penOption.color);
                          setStrokeTool("pen");
                          setShowPenMenu(false);
                        }}
                      >
                        <span className="slate-pen-swatch-fill" style={{ backgroundColor: penOption.color }} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              id="slate-btn-eraser"
              type="button"
              className={`slate-handle-btn ${strokeTool === "eraser" && workTab === "slate" ? "active" : ""}`}
              title="Eraser"
              onClick={() => {
                if (workTab !== "slate" && typeof setWorkTab === "function") setWorkTab("slate");
                setStrokeTool("eraser");
                setShowPenMenu(false);
                setShowPaperMenu(false);
              }}
            >
              <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                <path
                  d="M3 14l7-7a2 2 0 012.8 0l8.2 8.2a2 2 0 010 2.8l-4 4H9.2a2 2 0 01-1.4-.6L3 17a2 2 0 010-2.8z"
                  fill="currentColor"
                  opacity="0.25"
                />
                <path
                  d="M4.4 15.4l7-7a1 1 0 011.4 0l7.2 7.2a1 1 0 010 1.4L16 21H9.5a1 1 0 01-.7-.3l-4.4-4.4a1 1 0 010-1.4z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                <path d="M12 9l6 6" stroke="currentColor" strokeWidth="1.6" />
              </svg>
            </button>

            <div className="slate-handle-divider" />

            <button
              id="slate-btn-undo"
              type="button"
              className="slate-handle-btn"
              title="Undo last stroke"
              onClick={() => {
                setShowPaperMenu(false);
                setShowPenMenu(false);
                handleUndoStroke();
              }}
              disabled={!lines.length}
            >
              <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                <path
                  d="M9 7l-5 5 5 5M5 12h8a6 6 0 110 12h-2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <button
              id="slate-btn-redo"
              type="button"
              className="slate-handle-btn"
              title="Redo stroke"
              onClick={() => {
                setShowPaperMenu(false);
                setShowPenMenu(false);
                handleRedoStroke();
              }}
              disabled={!redoStack.length}
            >
              <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                <path
                  d="M15 7l5 5-5 5M19 12h-8a6 6 0 100 12h2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* 2. Slate Physical Frame - Blue Fountain Pen Ink Dashed Border */}
      <div className="slate-physical-frame">
        {/* 3. Utility Row: Erwin on left; Page format, Scroll, Clear, Math Editor / Slate toggle, Fullscreen on right */}
        <div className="slate-utility-row">
        <div className="slate-utility-left">
          <button
            id="slate-tutor-trigger"
            type="button"
            className={`slate-tutor-trigger ${tutorChatOpen ? "active" : ""}`}
            onClick={onTutorChatToggle}
            aria-expanded={Boolean(tutorChatOpen)}
            title="Ask Erwin"
          >
            <span className="slate-tutor-chat-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" className="slate-icon-svg">
                <path
                  d="M5 6.5A3.5 3.5 0 018.5 3h7A3.5 3.5 0 0119 6.5v4A3.5 3.5 0 0115.5 14H11l-4.5 3v-3A3.5 3.5 0 013 10.5v-4z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <path d="M8 7.5h8M8 10.5h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </span>
            <span className="slate-tutor-trigger-title">Erwin</span>
            <span className="slate-tutor-caret" aria-hidden="true">
              {tutorChatOpen ? "\u25b4" : "\u25be"}
            </span>
          </button>

          {/* Reverted left-docked expandable Erwin Chatbox */}
          {tutorChatOpen && (
            <div id="slate-tutor-chatbox" className={`slate-tutor-chatbox ${tutorModeMeta?.className || ""}`.trim()}>
              <div className="slate-tutor-chatbox-head">
                <div className="slate-tutor-chatbox-title-row">
                  <span className="slate-tutor-bulb-indicator" aria-hidden="true">💡</span>
                  <span className="slate-tutor-header-text">Erwin's Guidance</span>
                </div>
                <button
                  type="button"
                  className="slate-tutor-close-btn"
                  onClick={onTutorChatToggle}
                  title="Close Erwin"
                  aria-label="Close Erwin"
                >
                  ✕
                </button>
              </div>

              {isNormalHinting && (
                <div
                  className={`slate-tutor-loading-wave ${groupedTutorHistory.length > 0 ? "has-history" : ""}`.trim()}
                  aria-label="Erwin is thinking..."
                >
                  <span />
                  <span />
                  <span />
                </div>
              )}

              {groupedTutorHistory.length > 0 ? (
                <div className="slate-tutor-history">
                  {groupedTutorHistory.map((group, index) => {
                    const tutor = group?.tutor || {};
                    const primary = getTutorPrimaryGuidance(tutor);
                    const targetLabel = primary.kind === "error" ? "Check" : "Next step";
                    const isComplete =
                      primary.kind === "complete" ||
                      primary.label === "Done" ||
                      String(group?.status || "").toLowerCase() === "done" ||
                      String(group?.status || "").toLowerCase() === "complete" ||
                      tutor?.studentState === "complete" ||
                      tutor?.guidance?.recommendedPresentation === "complete" ||
                      /complete|congrat|fully solved/i.test(String(primary.title || ""));
                    return (
                      <div
                        key={group.id || index}
                        className={`slate-tutor-history-item ${primary.kind === "error" ? "is-error" : ""} ${index > 0 ? "has-divider" : ""}`}
                      >
                        <div className="slate-tutor-history-head">
                          <span>{formatTutorText(primary.title)}</span>
                          <span className={index > 0 ? "slate-tutor-history-previous" : "slate-tutor-history-kind"}>
                            {index > 0 ? "Previous" : primary.label}
                          </span>
                        </div>
                        {primary.message && <div className="analysis-text">{formatTutorText(primary.message)}</div>}
                        {primary.detail && formatTutorText(primary.detail) !== formatTutorText(primary.message) && (
                          <div className="analysis-text slate-tutor-detail">{formatTutorText(primary.detail)}</div>
                        )}
                        {primary.targetTitle &&
                          !["next step", "first step", "step", "hint"].includes(
                            String(primary.targetTitle).trim().toLowerCase()
                          ) && (
                            <div className="slate-tutor-inline-meta">
                              {targetLabel}: {formatTutorText(primary.targetTitle)}
                            </div>
                          )}

                        {!isComplete && Array.isArray(group.explanations) && group.explanations.length > 0 && (
                          <div className="slate-tutor-nested-explanations-list">
                            {group.explanations.map((expEntry, expIdx) => {
                              const expTutor = expEntry?.tutor || {};
                              const expPrimary = getTutorPrimaryGuidance(expTutor);
                              const expMsg = expPrimary.message || expTutor?.summary?.message || "";
                              const expTitle =
                                formatTutorText(expPrimary.title) &&
                                !["hint", "guidance", "note", "next step"].includes(
                                  String(expPrimary.title).toLowerCase().trim()
                                )
                                  ? formatTutorText(expPrimary.title)
                                  : "Deeper Breakdown";
                              return (
                                <div key={expEntry.id || `exp_${expIdx}`} className="slate-tutor-nested-explanation">
                                  <div className="slate-tutor-nested-explanation-head">
                                    <span className="slate-tutor-nested-bulb" aria-hidden="true">💡</span>
                                    <span className="slate-tutor-nested-title">{expTitle}</span>
                                  </div>
                                  {expMsg && (
                                    <div className="slate-tutor-nested-explanation-text">
                                      {formatTutorText(expMsg)}
                                    </div>
                                  )}
                                  {expPrimary.detail && formatTutorText(expPrimary.detail) !== formatTutorText(expMsg) && (
                                    <div className="slate-tutor-nested-explanation-detail">
                                      {formatTutorText(expPrimary.detail)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {index === 0 && isExplainingDeeper && (
                          <div className="slate-tutor-nested-explanation is-loading">
                            <div className="slate-tutor-nested-explanation-head">
                              <span className="slate-tutor-nested-bulb" aria-hidden="true">💡</span>
                              <span className="slate-tutor-nested-title">Unpacking explanation...</span>
                            </div>
                            <div className="slate-tutor-child-loading-wave" aria-label="Erwin is thinking...">
                              <span />
                              <span />
                              <span />
                            </div>
                          </div>
                        )}

                        {index === 0 && !isHinting && !isComplete && typeof onTutorHintClick === "function" && (
                          <div className="slate-tutor-explain-action-row">
                            <button
                              type="button"
                              className="slate-tutor-pill-btn explain"
                              onClick={() => onTutorHintClick({ intent: "explain_deeper" })}
                              title="Unpack and explain this hint deeper"
                            >
                              <svg
                                viewBox="0 0 20 20"
                                className="slate-tutor-explain-icon"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path
                                  d="M10 2.5a5.5 5.5 0 0 0-3.5 9.74V14a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1v-1.76A5.5 5.5 0 0 0 10 2.5z"
                                  fill="#fde68a"
                                  stroke="#d97706"
                                />
                                <line x1="8" y1="17.5" x2="12" y2="17.5" stroke="#d97706" />
                                <line x1="10" y1="1" x2="10" y2="2.5" stroke="#f59e0b" />
                                <line x1="4.5" y1="4.5" x2="5.5" y2="5.5" stroke="#f59e0b" />
                                <line x1="15.5" y1="4.5" x2="14.5" y2="5.5" stroke="#f59e0b" />
                              </svg>
                              <span>Explain this</span>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                !isHinting && (
                  <div className="analysis-text" style={{ padding: "6px 0", color: "#64748b" }}>
                    Tap the 💡 bulb on the slate to ask Erwin for guidance.
                  </div>
                )
              )}
            </div>
          )}
        </div>

        <div className="slate-utility-right" ref={toolbarMenuRef}>
          {typeof onOpenPhotoModal === "function" && (
            <button
              id="slate-btn-camera"
              type="button"
              className={`slate-icon-button ${isPhotoSubmissionActive ? "active" : ""}`}
              title={isPhotoSubmissionActive ? "View submitted solution photos" : "Upload or snap photos of your handwritten notebook solution"}
              onClick={onOpenPhotoModal}
            >
              <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                <path
                  d="M4 7h3l2-3h6l2 3h3a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="14" r="4" fill="none" stroke="currentColor" strokeWidth="1.8" />
              </svg>
            </button>
          )}

          {typeof setPaperStyle === "function" && (
            <div className="slate-paper-menu">
              <button
                id="slate-btn-paper"
                type="button"
                className={`slate-icon-button ${showPaperMenu ? "active" : ""}`}
                title={`Paper style: ${paperStyle === "ruled" ? "Ruled" : paperStyle === "grid" ? "Grid" : "Plain White"}`}
                onClick={() => {
                  setShowPaperMenu((prev) => !prev);
                  if (typeof setShowPenMenu === "function") setShowPenMenu(false);
                }}
              >
                <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                  <rect x="4" y="3" width="16" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
                  <line x1="8" y1="8" x2="16" y2="8" stroke="currentColor" strokeWidth="1.5" />
                  <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="1.5" />
                  <line x1="8" y1="16" x2="16" y2="16" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </button>
              {showPaperMenu && (
                <div className="slate-paper-dropdown" role="menu">
                  <button
                    type="button"
                    className={`slate-paper-dropdown-item ${paperStyle === "ruled" ? "active" : ""}`}
                    onClick={() => {
                      setPaperStyle("ruled");
                      setShowPaperMenu(false);
                    }}
                  >
                    <span className="paper-dropdown-icon ruled-icon" aria-hidden="true">≡</span>
                    <span>Ruled Paper</span>
                  </button>
                  <button
                    type="button"
                    className={`slate-paper-dropdown-item ${paperStyle === "grid" ? "active" : ""}`}
                    onClick={() => {
                      setPaperStyle("grid");
                      setShowPaperMenu(false);
                    }}
                  >
                    <span className="paper-dropdown-icon grid-icon" aria-hidden="true">⊞</span>
                    <span>Grid Paper</span>
                  </button>
                  <button
                    type="button"
                    className={`slate-paper-dropdown-item ${paperStyle === "white" ? "active" : ""}`}
                    onClick={() => {
                      setPaperStyle("white");
                      setShowPaperMenu(false);
                    }}
                  >
                    <span className="paper-dropdown-icon white-icon" aria-hidden="true">◻</span>
                    <span>Plain White</span>
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            id="slate-btn-scroll"
            type="button"
            className={`slate-icon-button ${isScrollMode ? "active" : ""}`}
            title="Hand scroll"
            onClick={() => {
              if (workTab !== "slate" && typeof setWorkTab === "function") setWorkTab("slate");
              setStrokeTool(isScrollMode ? "pen" : "scroll");
              setShowPenMenu(false);
              setShowPaperMenu(false);
            }}
          >
            <svg viewBox="0 0 150 150" className="slate-icon-svg slate-grab-hand-svg" aria-hidden="true">
              <path d="M 74 15 L 78 15 L 79 16 L 83 17 L 88 24 L 89 23 L 97 23 L 99 24 L 102 27 L 104 31 L 106 30 L 110 30 L 111 31 L 115 32 L 118 36 L 118 38 L 119 39 L 119 66 L 118 67 L 118 70 L 107 91 L 100 96 L 48 96 L 43 92 L 43 91 L 37 83 L 36 80 L 32 75 L 31 71 L 30 70 L 30 58 L 31 57 L 32 53 L 35 50 L 37 50 L 38 49 L 44 49 L 45 48 L 45 28 L 46 27 L 47 23 L 50 20 L 52 20 L 53 19 L 59 19 L 61 20 L 65 24 L 67 20 L 70 17 L 74 15 Z" fill="currentColor" />
              <rect x="38" y="109" width="74" height="26" rx="13" fill="currentColor" />
            </svg>
          </button>

          <div className="slate-clear-menu">
            <button
              id="slate-btn-clear"
              type="button"
              className="slate-icon-button slate-clear-button"
              title="Clear slate"
              onClick={() => {
                setShowPenMenu(false);
                setShowPaperMenu(false);
                if (typeof onClearSlate === "function") onClearSlate();
              }}
              disabled={clearSlateDisabled}
              aria-expanded={Boolean(showClearConfirm)}
            >
              <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
                <path d="M7.2 16.8L16.8 7.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            {showClearConfirm && (
              <div className="clear-confirm-popover clear-confirm-popover-toolbar" role="dialog" aria-label="Clear handwritten slate confirmation">
                <div className="clear-confirm-title">Clear handwritten slate?</div>
                <div className="clear-confirm-actions">
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={onCancelClearSlate}
                  >
                    Cancel
                  </button>
                  <button className="primary-button" type="button" onClick={onConfirmClearSlate}>
                    Confirm
                  </button>
                </div>
              </div>
            )}
          </div>

          {typeof setWorkTab === "function" && (
            <button
              id="slate-btn-matheditor"
              type="button"
              className={`slate-icon-button ${workTab === "editor" ? "active" : ""}`}
              title={workTab === "editor" ? "Return to Handwritten Slate" : "Switch to Math Editor"}
              onClick={() => {
                setShowPenMenu(false);
                setShowPaperMenu(false);
                setWorkTab(workTab === "slate" ? "editor" : "slate");
              }}
            >
              {workTab === "editor" ? (
                <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                  <path
                    d="M4 17.2V20h2.8L17.6 9.2l-2.8-2.8L4 17.2z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <path
                    d="M13.9 7.1l2.8 2.8M15.6 5.4l2.8 2.8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              ) : (
                <span className="slate-matheditor-symbol" aria-hidden="true">
                  ∑
                </span>
              )}
            </button>
          )}

          <button
            id="slate-btn-fullscreen"
            type="button"
            className="slate-icon-button"
            title={slateFullScreen ? "Exit full screen" : "Enter full screen"}
            onClick={() => {
              setShowPenMenu(false);
              setShowPaperMenu(false);
              setSlateFullScreen((prev) => !prev);
            }}
          >
            {slateFullScreen ? (
              <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                <path
                  d="M9 3H3v6M15 3h6v6M3 15v6h6M21 15v6h-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="slate-icon-svg" aria-hidden="true">
                <path
                  d="M9 3H3v6M15 3h6v6M3 15v6h6M21 15v6h-6M9 9L3 3M15 9l6-6M9 15l-6 6M15 15l6 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
      {workTab === "slate" ? (
        <div className="slate-stage-wrapper">
          {isPhotoSubmissionActive ? (
            <PhotoNotebookViewport
              images={photoSubmissionPages}
              detectedStepBoxes={detectedStepBoxes}
              stepDiagnostics={stepDiagnostics}
              selectedStep={selectedDiagStep}
              onStepSelect={setSelectedDiagStep}
              isAnalyzing={isAnalyzing}
              analysisStatus={analysisStatus}
              photoProcessingPhase={photoProcessingPhase}
              onOpenPhotoModal={onOpenPhotoModal}
            />
          ) : (
            <div
              className={`slate-stage ${isScrollMode ? "scroll-mode" : isDrawing ? "draw-mode" : "ink-mode"} profile-${slateProfile} canvas-paper-${paperStyle || "white"}`}
          ref={slateRef}
          onPointerDown={handleStagePointerDown}
          onPointerMove={handleStagePointerMove}
          onPointerUp={handleStagePointerUp}
          onPointerCancel={handleStagePointerUp}
          onContextMenu={(e) => e.preventDefault()}
          onSelectStart={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        >
        <div className="slate-stage-content">
          <div
            ref={canvasPaneRef}
            className={`slate-canvas-pane ${isScrollMode ? "scroll-mode" : "ink-mode"} profile-${slateProfile}`}
            style={{ width: `${stageCanvasWidth}px`, minWidth: `${stageCanvasWidth}px` }}
          >
            {plainCanvas && (!lines || lines.length === 0) && (
              <div
                id="slate-handwritten-guide"
                className={`slate-handwritten-guide ${isDrawing ? "is-drawing" : ""}`}
                aria-hidden="true"
              >
                <div className="slate-handwritten-line slate-line-1">
                  Please start solving by writing your steps on the slate
                </div>
                <div className="slate-handwritten-gap-indicator">
                  <svg width="14" height="24" viewBox="0 0 14 24" fill="none" className="slate-gap-arrow-svg">
                    <path d="M7 2 L7 22" stroke="currentColor" strokeWidth="1.4" strokeDasharray="2 2" />
                    <path d="M4 6 L7 2 L10 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4 18 L7 22 L10 18" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="slate-gap-arrow-label">~30px gap</span>
                </div>
                <div className="slate-handwritten-line slate-line-2">
                  Please leave a small gap between lines for better results
                </div>
              </div>
            )}
            {plainCanvas && clusters.length > 0 && (
              <div className="slate-left-step-rail" aria-label="Step Rail">
                {clusters.map((cluster) => {
                  const b = cluster.bounds;
                  if (!b || b.width < 8) return null;
                  const top = Math.round((b.minY + b.maxY) / 2);
                  const diag = (slateRowDiagnostics || []).find((d) => Number(d.stepNo) === Number(cluster.stepNo));
                  const status = String(diag?.diagnostic?.status || "");
                  const isIncorrect = status === "incorrect";
                  const isIncomplete = status === "incomplete";
                  const hasDiag = Boolean(diag && diag.diagnostic);
                  const detailText = diag?.diagnostic?.reason || diag?.statusMeta?.label || `Step ${cluster.stepNo}`;
                  const isSelected = selectedDiagStep === cluster.stepNo;

                  return (
                    <div
                      key={`step_rail_${cluster.id || cluster.stepNo}`}
                      className="slate-step-rail-item"
                      style={{ top: `${top}px` }}
                    >
                      <button
                        type="button"
                        className={`slate-step-marker ${
                          hasDiag
                            ? `${diag.statusMeta?.className || ""} ${isIncorrect ? "shape-triangle" : isIncomplete ? "shape-rhombus" : "shape-circle"}`
                            : "default-step-badge"
                        } ${isSelected ? "is-selected" : ""}`}
                        title={detailText}
                        aria-label={`Step ${cluster.stepNo}: ${detailText}`}
                        onClick={() =>
                          setSelectedDiagStep((prev) => (Number(prev) === Number(cluster.stepNo) ? null : cluster.stepNo))
                        }
                        onFocus={() => setSelectedDiagStep(cluster.stepNo)}
                      >
                        <span className="slate-step-marker-number">{cluster.stepNo}</span>
                        {hasDiag && (
                          <span
                            className={`slate-step-popover tutor-note ${diag.statusMeta?.className || ""} ${
                              isSelected ? "is-visible" : ""
                            }`}
                          >
                            <strong className="slate-step-popover-title">
                              Step {cluster.stepNo} - {diag.statusMeta?.label || status}
                            </strong>
                            <span>{detailText}</span>
                          </span>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <button
              id="slate-btn-hint"
              type="button"
              className={`slate-hint-bulb ${isHinting ? "is-checking" : ""}`}
              style={{ top: `${tutorHintTop}px` }}
              onClick={(event) => {
                if (typeof onTutorChatOpen === "function") {
                  onTutorChatOpen();
                }
                if (typeof onTutorHintClick === "function") {
                  onTutorHintClick(event);
                }
              }}
              disabled={tutorHintDisabled}
              title={
                isHinting
                  ? "Erwin is thinking..."
                  : plainCanvas && clusters.length > 0
                  ? `Ask Erwin for Step ${clusters.length + 1}`
                  : "Ask Erwin for guidance"
              }
              aria-label="Ask Erwin for guidance"
            >
              <svg viewBox="0 0 24 24" className="slate-bulb-svg" aria-hidden="true">
                <path
                  d="M8.5 15.2c-.8-1-1.5-2.2-1.5-3.8A5 5 0 1117 11.4c0 1.6-.7 2.8-1.5 3.8-.5.6-.9 1.1-1 1.8h-5c-.1-.7-.5-1.2-1-1.8z"
                  fill="currentColor"
                />
                <path d="M9.5 20h5M10 22h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            <Stage
              ref={stageRef}
              width={stageCanvasWidth}
              height={stageSize.height}
              listening={!isScrollMode && !isPhotoSubmissionActive}
              onPointerDown={(isScrollMode || isPhotoSubmissionActive) ? undefined : handlePointerDown}
              onPointerMove={(isScrollMode || isPhotoSubmissionActive) ? undefined : handlePointerMove}
              onPointerUp={(isScrollMode || isPhotoSubmissionActive) ? undefined : handlePointerUp}
              onPointerCancel={
                (isScrollMode || isPhotoSubmissionActive)
                  ? undefined
                  : (event) => handlePointerUp(event, { cancelled: true })
              }
            >
              {!plainCanvas && (
                <Layer listening={false}>
                  {[0, ...laneBounds.map((lane) => lane.bottom)].map((y, idx) => (
                    <Line
                      key={`rule_${idx}_${Math.round(y)}`}
                      points={[0, y, stageCanvasWidth, y]}
                      stroke={ruleStroke}
                      strokeWidth={ruleStrokeWidth}
                      dash={ruleDash}
                      lineCap="round"
                      listening={false}
                    />
                  ))}
                </Layer>
              )}
              <Layer>
                {lines.map((line, index) => (
                  <Line
                    key={line.id || index}
                    points={line.points}
                    stroke={line.tool === "eraser" ? "#000000" : line.color}
                    strokeWidth={line.width || (line.tool === "eraser" ? 28 : 2.5)}
                    tension={0}
                    lineCap="round"
                    lineJoin="round"
                    globalCompositeOperation={line.tool === "eraser" ? "destination-out" : "source-over"}
                    listening={false}
                    perfectDrawEnabled={false}
                  />
                ))}
                {(Array.isArray(remotePreviewLines) ? remotePreviewLines : []).map((line, index) => (
                  <Line
                    key={`remote_preview_${line.strokeId || line.id || index}`}
                    points={Array.isArray(line.points) ? line.points : []}
                    stroke={line.tool === "eraser" ? "#000000" : line.color}
                    strokeWidth={line.width || (line.tool === "eraser" ? 28 : 2.5)}
                    tension={0}
                    lineCap="round"
                    lineJoin="round"
                    globalCompositeOperation={line.tool === "eraser" ? "destination-out" : "source-over"}
                    opacity={0.82}
                    listening={false}
                    perfectDrawEnabled={false}
                  />
                ))}
              </Layer>
            </Stage>
            <canvas
              ref={activeStrokeCanvasRef}
              className={`slate-active-stroke-canvas ${isScrollMode ? "scroll-mode" : "ink-mode"} profile-${slateProfile}`}
              width={stageCanvasWidth}
              height={stageSize.height}
              aria-hidden="true"
            />
            {!plainCanvas && (
              <div className="slate-rule-handle-layer">
                {!isScrollMode && laneBounds.slice(0, -1).map((lane) => (
                  <button
                    key={`rule_handle_${lane.index}`}
                    id={lane.index === 0 ? "slate-rule-line" : undefined}
                    type="button"
                    className={`slate-rule-handle ${laneDragBoundary === lane.index ? "active" : ""}`}
                    style={{ top: `${Math.max(0, Math.round(lane.bottom))}px` }}
                    title="Drag to resize this writing row"
                    onPointerDown={(event) => handleRuleDragStart(lane.index, event)}
                  />
                ))}
              </div>
            )}
            {plainCanvas && Array.isArray(stepClusters) && stepClusters.length > 0 && (
              <div className="slate-step-bracket-layer">
                {stepClusters.map((cluster) => {
                  const b = cluster.bounds;
                  if (!b || b.width < 8) return null;
                  return (
                    <div
                      key={cluster.id || `cluster_${cluster.stepNo}`}
                      className={`slate-step-bracket ${cluster.contentKind === "diagram" ? "is-diagram" : ""}`}
                      style={{
                        left: `${Math.max(4, Math.round(b.minX - 12))}px`,
                        top: `${Math.max(4, Math.round(b.minY - 12))}px`,
                        width: `${Math.min(stageCanvasWidth - 14, Math.round(b.width + 24))}px`,
                        height: `${Math.round(b.height + 24)}px`
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {!plainCanvas && showSlateDiagnostics && (
          <div
            ref={diagnosticRailRef}
            className="slate-diagnostic-overlay"
            style={{
              height: `${stageSize.height}px`,
              width: `${diagnosticRailWidth}px`,
              transform: `translateX(${viewportScrollLeft}px)`
            }}
          >
            {slateRowDiagnostics.map((item) => {
              let top = 0;
              if (plainCanvas && Array.isArray(stepClusters)) {
                const cluster = stepClusters.find((c) => Number(c.stepNo) === Number(item.stepNo));
                if (cluster?.bounds) {
                  top = Math.round((cluster.bounds.minY + cluster.bounds.maxY) / 2);
                }
              }
              if (!top) {
                const lane = laneBounds.find((l) => Number(l.index) === Number(item.laneIndex));
                if (!lane) return null;
                top = Math.round((lane.top + lane.bottom) / 2) + 12;
              }
              const status = String(item?.diagnostic?.status || "");
              const isIncorrect = status === "incorrect";
              const isIncomplete = status === "incomplete";
              const detailText = item.diagnostic?.reason || item.statusMeta.label;
              return (
                <button
                  key={`slate_step_tag_${item.laneIndex}`}
                  type="button"
                  className={`slate-step-marker ${item.statusMeta.className} ${isIncorrect ? "shape-triangle" : isIncomplete ? "shape-rhombus" : "shape-circle"
                    } ${selectedDiagStep === item.stepNo ? "is-selected" : ""}`}
                  style={{ top: `${top}px` }}
                  title={detailText}
                  aria-label={`Step ${item.stepNo}: ${item.statusMeta.label}`}
                  onClick={() =>
                    setSelectedDiagStep((prev) => (Number(prev) === Number(item.stepNo) ? null : item.stepNo))
                  }
                  onFocus={() => setSelectedDiagStep(item.stepNo)}
                >
                  <span className="slate-step-marker-number">{item.stepNo}</span>
                  <span
                    className={`slate-step-popover tutor-note ${item.statusMeta.className} ${selectedDiagStep === item.stepNo ? "is-visible" : ""
                      }`}
                  >
                    <strong className="slate-step-popover-title">
                      Step {item.stepNo} - {item.statusMeta.label}
                    </strong>
                    <span>{detailText}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
            </div>
          )}
        </div>
      ) : (
      <div className="slate-editor-surface">
        {editorContent}
      </div>
    )}
        {/* 5. Bottom Action Bar: Live OCR on left, Analyze Button on right */}
        <div className="slate-bottom-bar">
          <div className="slate-bottom-left">
            {liveOcrStatus && <span className="slate-live-ocr-status">{liveOcrStatus}</span>}
          </div>
          <div className="slate-bottom-right">
            {analyzeActions}
          </div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(SlateCanvasPanel);
